import React from "react";
import styled, { css } from "styled-components";
import { Trash2 } from "react-feather";
import {
  Banner,
  Button,
  Input,
  Select,
  SelectOption,
} from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../strings";
import {
  currentFieldNameQuery,
  filterTemplateTokenNames,
  insertConditionField,
} from "../templateTokens";
import TokenChip, { TokenCarousel, TokenRow } from "../TokenChip";
import { useConditionSyntaxCheck } from "../mapping/service";
import ConditionSyntaxHelpModal from "./ConditionSyntaxHelpModal";

/** One Conditional-mode rule as edited locally — `then.field` is never stored here since it's
 *  always the row's own destination field (derived at save time in `FieldsScreen.tsx`'s
 *  `handleSave`), so there's nothing for a separate destination picker to choose. */
export interface LocalConditionalRule {
  when: string;
  set: string;
  mode: "append" | "overwrite";
}

export interface ConditionalRuleFieldOption {
  key: string;
}

const MODE_OPTIONS: SelectOption[] = [
  {
    value: "overwrite",
    label: SPOTTER_STRINGS.fields.conditional.modeOverwrite,
  },
  { value: "append", label: SPOTTER_STRINGS.fields.conditional.modeAppend },
];

const RuleWrap = styled.div`
  border: 1px solid var(--spotterLine);
  border-radius: 9px;
  padding: 12px;
  margin-bottom: 10px;
  background: var(--spotterPaper);
`;

const RuleHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
`;

const RuleFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SetModeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 10px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

// `RuleFields` already spaces its children with its own `gap: 10px` — `TokenRow`'s own margin
// would double that up, unlike Formatting/Rename's plain-block-flow parents.
const FieldTokenRow = styled(TokenRow)`
  margin: 0;
`;

// Rendered above the `when` Input as its own full-width row instead of via Input's `label` prop
// — that prop's wrapper (`@cinesys/ui`'s `Label` component, its `LabelText` span) shrink-wraps
// its content, so a `width: 100%` child of it has no real containing-block width to fill and
// can't actually push the status readout to the right edge. This wrapping `<div>` in
// `RuleFields` (a flex column, default `align-items: stretch`) gives it one.
const WhenLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 5px;
  font-size: 14.5px;
  color: var(--gray300);
`;

const WhenLabelLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const StatusInline = styled.span<{ $tone: "ok" | "muted" | "error" }>`
  font-size: 12.5px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(p) =>
    p.$tone === "ok"
      ? "var(--spotterOk)"
      : p.$tone === "error"
      ? "var(--spotterDanger)"
      : "var(--spotterInkFaint)"};
`;

const StatusDot = styled.span<{
  $tone: "ok" | "muted" | "error";
  $pulse?: boolean;
}>`
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: currentColor;
  flex: 0 0 auto;

  ${(p) =>
    p.$pulse &&
    css`
      animation: spotter-condition-checking-pulse 1s ease-in-out infinite;
    `}

  @keyframes spotter-condition-checking-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.25;
    }
  }
`;

// A button, not a link out to `xlucene-parser`'s own docs — those are written for implementing/
// extending the parser (fields/AST/regex/...), not for a customer typing a one-line condition.
// `ConditionSyntaxHelpModal` is this app's own plain-language cheat sheet instead.
const SyntaxHelpButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  color: var(--spotterInkFaint);
  text-decoration: underline;

  &:hover {
    color: var(--spotterFacial);
  }
`;

interface ConditionalRuleRowProps {
  value: LocalConditionalRule;
  onChange: (value: LocalConditionalRule) => void;
  onRemove: () => void;
  canRemove: boolean;
  fieldOptions: ConditionalRuleFieldOption[];
  portalTarget: HTMLDivElement | null;
}

/** One rule row in a Conditional-mode field's editor — a plain-text `when` condition (no Monaco/
 *  syntax highlighting), a `then.set` value, an append/overwrite mode, and a live syntax-valid/
 *  invalid check against `POST /mapping/preview-condition` (debounced inside
 *  `useConditionSyntaxCheck`, so this only hits the network after the customer pauses typing). */
const ConditionalRuleRow: React.FC<ConditionalRuleRowProps> = ({
  value,
  onChange,
  onRemove,
  canRemove,
  fieldOptions,
  portalTarget,
}) => {
  const strings = SPOTTER_STRINGS.fields.conditional;
  const syntaxCheck = useConditionSyntaxCheck(value.when);
  const [showSyntaxHelp, setShowSyntaxHelp] = React.useState(false);
  const whenInputRef = React.useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const filteredFieldOptions = filterTemplateTokenNames(
    fieldOptions.map((f) => f.key),
    currentFieldNameQuery(value.when)
  );

  let tone: "ok" | "muted" | "error" = "muted";
  let statusText: string = strings.checking;
  if (!syntaxCheck.isChecking) {
    if (syntaxCheck.data) {
      if (syntaxCheck.data.valid) {
        tone = "ok";
        statusText = strings.validSyntax;
      } else {
        tone = "error";
        statusText = strings.invalidSyntax;
      }
    } else if (syntaxCheck.isError) {
      tone = "error";
      statusText = strings.invalidSyntax;
    }
  }

  return (
    <RuleWrap>
      {canRemove && (
        <RuleHeader>
          <Button
            label={strings.removeRuleButton}
            icon={Trash2}
            type="danger"
            borderless
            onClick={onRemove}
          />
        </RuleHeader>
      )}
      <RuleFields>
        <div>
          <WhenLabelRow>
            <WhenLabelLeft>
              {strings.whenLabel}
              <SyntaxHelpButton
                type="button"
                onClick={() => setShowSyntaxHelp(true)}
              >
                {strings.whenSyntaxDocsLabel}
              </SyntaxHelpButton>
            </WhenLabelLeft>
            <StatusInline $tone={tone}>
              <StatusDot $tone={tone} $pulse={syntaxCheck.isChecking} />
              {statusText}
            </StatusInline>
          </WhenLabelRow>
          <Input
            noPadding
            minWidth={0}
            placeholder={strings.whenPlaceholder}
            value={value.when}
            onChange={(when) => onChange({ ...value, when })}
            ref={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
              whenInputRef.current = el;
            }}
          />
        </div>
        {fieldOptions.length > 0 && (
          <FieldTokenRow>
            {filteredFieldOptions.length > 0 ? (
              <TokenCarousel
                gap={8}
                justify="start"
                fadeColor="var(--spotterPaper)"
              >
                {filteredFieldOptions.map((key) => (
                  <TokenChip
                    key={key}
                    token={key}
                    onClick={() => {
                      onChange({
                        ...value,
                        when: insertConditionField(value.when, `${key}:`),
                      });
                      whenInputRef.current?.focus();
                    }}
                  />
                ))}
              </TokenCarousel>
            ) : (
              <Banner placeholder={SPOTTER_STRINGS.noTokenOptions} />
            )}
          </FieldTokenRow>
        )}
        <SetModeRow>
          <Input
            label={strings.setLabel}
            noPadding
            minWidth={0}
            placeholder={strings.setPlaceholder}
            value={value.set}
            onChange={(set) => onChange({ ...value, set })}
          />
          <Select
            label={strings.modeLabel}
            options={MODE_OPTIONS}
            value={value.mode}
            onChange={(option) => {
              const mode = (option as SelectOption)?.value as
                | "append"
                | "overwrite"
                | undefined;
              if (mode) {
                onChange({ ...value, mode });
              }
            }}
            portalTarget={portalTarget}
            noPadding
          />
        </SetModeRow>
      </RuleFields>
      {showSyntaxHelp && (
        <ConditionSyntaxHelpModal onClose={() => setShowSyntaxHelp(false)} />
      )}
    </RuleWrap>
  );
};

export default ConditionalRuleRow;
